/**
 * Dynamically updates the browser favicon.
 *
 * Priority:
 *  1. Admin logo URL (if provided)
 *  2. Canvas-drawn colored circle with brand initial (fallback)
 *
 * @param logoUrl  The admin-uploaded logo URL, or null/undefined to draw the circle fallback.
 * @param color    Hex/CSS color for the circle fallback (defaults to --primary-color CSS var).
 * @param initial  Letter to draw in the circle center (defaults to 'B').
 */
const APP_NAME = process.env.REACT_APP_NAME || '';
const DEFAULT_INITIALS = APP_NAME.trim()
  ? APP_NAME.trim().split(/\s+/).map(w => w[0].toUpperCase()).join('')
  : 'PC';

export function updateFavicon(
  logoUrl?: string | null,
  color?: string,
  initial?: string
): void {
  // Always prefer the live server endpoint so the icon tracks the current DB theme.
  // Only fall back to canvas if we're in dev (no /pwa-icon.svg served by backend).
  const isDev = process.env.NODE_ENV !== 'production';

  let href: string;
  if (logoUrl) {
    href = logoUrl;
  } else if (!isDev) {
    // Production: use the dynamic server endpoint — always matches current theme + logo
    href = `/pwa-icon.svg?t=${Date.now()}`;
  } else {
    // Development: draw with canvas using current CSS vars
    const styles = getComputedStyle(document.documentElement);
    const bg = color || styles.getPropertyValue('--primary-color').trim() || '#3a9397';
    const fg = styles.getPropertyValue('--secondary-color').trim() || '#ff9800';
    href = drawCircleFavicon(bg, fg, initial || DEFAULT_INITIALS);
  }

  // Update both the SVG and regular icon links
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]')
    || document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = logoUrl ? '' : 'image/svg+xml';
  link.href = href;
}

function drawCircleFavicon(bg: string, fg: string, letters: string): string {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = bg;
  ctx.fill();

  const fontSize = letters.length > 2 ? Math.round(size * 0.3) : Math.round(size * 0.42);
  ctx.fillStyle = fg;
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(letters.toUpperCase(), size / 2, size / 2 + 1);

  return canvas.toDataURL('image/png');
}
